/**
 * The mark of one place a gardener can be found, in that place's colour.
 *
 * Small by design: a mark beside a handle, not a logo. The geometry and the
 * colour live in socialLinks, shared with the share frame, which draws the
 * same mark onto a canvas.
 *
 * Used by: the share dialog, the share and garden pages, the season review,
 * and the profile's links and public page cards.
 *
 * @module SocialIcon
 */

import React from 'react';
import { SOCIAL_MARKS, type LinkKey } from '@/shared/lib/socialLinks';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** Which platform's mark, how big, and whether it is read out. */
interface SocialIconProps {
  /** The profile link field whose platform this is. */
  name: LinkKey;
  /** Pixels. */
  size?: number;
  /** Extra classes for the SVG. */
  className?: string;
  /** Read out where the mark is the only thing saying which platform. */
  labelled?: boolean;
  /** In the surrounding text colour instead of the platform's own (the landing page). */
  mono?: boolean;
}

/**
 * The platform's mark in its own colour (or the text colour when mono); hidden from screen readers unless
 * labelled, when it is read out by the name the profile's links use
 * ("Website", "Sitio web"; the platforms keep their own names).
 */
export const SocialIcon: React.FC<SocialIconProps> = ({ name, size = 14, className, labelled = false, mono = false }) => {
  const { reactTranslate: t } = useTranslation();
  const mark = SOCIAL_MARKS[name];
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      style={mono ? { flexShrink: 0 } : { color: mark.color, flexShrink: 0 }}
      role={labelled ? 'img' : undefined}
      aria-label={labelled ? t(`profile.links.${name}`) : undefined}
      aria-hidden={labelled ? undefined : true}
    >
      <path d={mark.path} fill="currentColor" />
    </svg>
  );
};
